import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../store";
import { GET } from "../../config/httpMain";
import {
    ModulePermissionCustomizedPackages,
    ModulePermissionEnquiryNow,
    ModulePermissionFixedPackages,
} from "../../constants/constant";
import EnquiryAPIURL from "../enquiry/enquiryAPIURL";
import PackageAPIURL from "../packages/packageAPIURL";
import type { EnquiryModel } from "../enquiry/interfaceEnquiry";
import {
    filterEnquirySearchItems,
    type EnquirySearchItem,
} from "../../utility/enquirySearch";
import {
    filterPackageSearchItems,
    type PackageSearchItem,
    type PackageSearchRow,
    type PackageSearchType,
} from "../../utility/packageSearch";
import {
    buildNavSearchItems,
    filterNavSearchItems,
    type NavSearchItem,
} from "../../utility/navMenuSearch";

type NavSearchResult = NavSearchItem & { kind: "nav" };
type GlobalSearchResult = NavSearchResult | EnquirySearchItem | PackageSearchItem;

type GlobalNavSearchProps = {
    handleNavFunc: (url: string) => void;
};

export default function GlobalNavSearch({ handleNavFunc }: GlobalNavSearchProps) {
    const userSlice = useAppSelector((state: any) => state.userSlice);
    const modulePermission = userSlice?.user?.modulePermission;
    const companyCode = userSlice?.user?.userdetail?.companyCode;
    const userId = userSlice?.user?.userdetail?.id;

    const [query, setQuery] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [enquiries, setEnquiries] = useState<EnquiryModel[]>([]);
    const [packages, setPackages] = useState<PackageSearchRow[]>([]);

    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const canSearchEnquiries = useMemo(() => {
        const perm = (modulePermission || []).find(
            (f: any) => f.moduleCode?.toLowerCase() === ModulePermissionEnquiryNow
        );
        return !!perm?.view;
    }, [modulePermission]);

    const packageTypesAllowed = useMemo(() => {
        const types: PackageSearchType[] = [];
        const perms = modulePermission || [];
        if (perms.some((f: any) => f.moduleCode?.toLowerCase() === ModulePermissionCustomizedPackages && f.view)) {
            types.push("customized");
        }
        if (perms.some((f: any) => f.moduleCode?.toLowerCase() === ModulePermissionFixedPackages && f.view)) {
            types.push("fixed");
        }
        return types;
    }, [modulePermission]);

    const allItems = useMemo(() => buildNavSearchItems(modulePermission), [modulePermission]);

    const navResults = useMemo(
        () => filterNavSearchItems(allItems, query, 8).map((item) => ({ ...item, kind: "nav" as const })),
        [allItems, query]
    );

    const enquiryResults = useMemo(
        () => (canSearchEnquiries ? filterEnquirySearchItems(enquiries, query) : []),
        [canSearchEnquiries, enquiries, query]
    );

    const packageResults = useMemo(
        () => (packageTypesAllowed.length ? filterPackageSearchItems(packages, query) : []),
        [packageTypesAllowed.length, packages, query]
    );

    const results: GlobalSearchResult[] = useMemo(
        () => [...navResults, ...packageResults, ...enquiryResults],
        [navResults, packageResults, enquiryResults]
    );

    const close = useCallback(() => {
        setIsOpen(false);
        setActiveIndex(0);
    }, []);

    const selectItem = useCallback(
        (item: GlobalSearchResult) => {
            if (!item.url) return;
            handleNavFunc(item.url);
            setQuery("");
            close();
            inputRef.current?.blur();
        },
        [close, handleNavFunc]
    );

    useEffect(() => {
        if (!canSearchEnquiries || !companyCode) {
            setEnquiries([]);
            return;
        }

        let cancelled = false;

        GET(`${EnquiryAPIURL.GetEnquiryNow}?CompanyCode=${companyCode}`)
            .then((response: any) => {
                if (cancelled || !response?.data?.isSuccess) return;
                let listArr: EnquiryModel[] = response?.data?.result || [];
                if (userId !== 1) {
                    listArr = listArr.filter((row) => row.statusCode !== 4);
                }
                setEnquiries(listArr);
            })
            .catch(() => {
                if (!cancelled) setEnquiries([]);
            });

        return () => {
            cancelled = true;
        };
    }, [canSearchEnquiries, companyCode, userId]);

    useEffect(() => {
        if (!packageTypesAllowed.length || !companyCode) {
            setPackages([]);
            return;
        }

        let cancelled = false;

        Promise.all(
            packageTypesAllowed.map((type) =>
                GET(`${PackageAPIURL.GetPackageList}?CompanyCode=${companyCode}&Type=${type}`)
                    .then((response: any) => {
                        if (!response?.data?.isSuccess) return [] as PackageSearchRow[];
                        let listArr = (response?.data?.result || []) as PackageSearchRow[];
                        if (userId !== 1) {
                            listArr = listArr.filter((row: any) => row.isDelete === false);
                        }
                        return listArr.map((row) => ({ ...row, packageType: type }));
                    })
                    .catch(() => [] as PackageSearchRow[])
            )
        ).then((lists) => {
            if (!cancelled) setPackages(lists.flat());
        });

        return () => {
            cancelled = true;
        };
    }, [packageTypesAllowed, companyCode, userId]);

    useEffect(() => {
        setActiveIndex(0);
    }, [query]);

    useEffect(() => {
        const onDocMouseDown = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) close();
        };
        document.addEventListener("mousedown", onDocMouseDown);
        return () => document.removeEventListener("mousedown", onDocMouseDown);
    }, [close]);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                inputRef.current?.focus();
                setIsOpen(true);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            setQuery("");
            close();
            inputRef.current?.blur();
            return;
        }

        if (!results.length) return;

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => (i + 1) % results.length);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => (i - 1 + results.length) % results.length);
        } else if (e.key === "Enter") {
            e.preventDefault();
            const item = results[activeIndex];
            if (item) selectItem(item);
        }
    };

    const showDropdown = isOpen && query.trim().length > 0;
    const packageOffset = navResults.length;
    const enquiryOffset = navResults.length + packageResults.length;

    return (
        <div className="tms-global-search" ref={rootRef}>
            <div className="tms-global-search__field">
                <i className="ti-search tms-global-search__icon" aria-hidden="true" />
                <input
                    ref={inputRef}
                    type="search"
                    className="tms-global-search__input"
                    placeholder="Search pages, packages & enquiries..."
                    value={query}
                    aria-label="Search pages, packages, and enquiries"
                    aria-expanded={showDropdown}
                    aria-controls="tms-global-search-results"
                    aria-autocomplete="list"
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setIsOpen(true);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={onInputKeyDown}
                />
                <kbd className="tms-global-search__shortcut" aria-hidden="true">
                    Ctrl K
                </kbd>
            </div>

            {showDropdown && (
                <div
                    id="tms-global-search-results"
                    className="tms-global-search__dropdown"
                    role="listbox"
                >
                    {results.length === 0 ? (
                        <div className="tms-global-search__empty">No pages, packages, or enquiries found</div>
                    ) : (
                        <>
                            {navResults.length > 0 && (
                                <div className="tms-global-search__section">Pages</div>
                            )}
                            {navResults.map((item, index) => {
                                const path =
                                    item.breadcrumb.length > 1
                                        ? item.breadcrumb.slice(0, -1).join(" › ")
                                        : item.moduleName;

                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        role="option"
                                        aria-selected={index === activeIndex}
                                        className={`tms-global-search__option ${index === activeIndex ? "active" : ""} ${!item.url ? "disabled" : ""}`}
                                        disabled={!item.url}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onClick={() => selectItem(item)}
                                    >
                                        <i className={`${item.icon} tms-global-search__option-icon`} aria-hidden="true" />
                                        <span className="tms-global-search__option-body">
                                            <span className="tms-global-search__option-label">{item.label}</span>
                                            <span className="tms-global-search__option-path">{path}</span>
                                        </span>
                                    </button>
                                );
                            })}
                            {packageResults.length > 0 && (
                                <div className="tms-global-search__section">Packages</div>
                            )}
                            {packageResults.map((item, index) => {
                                const flatIndex = packageOffset + index;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        role="option"
                                        aria-selected={flatIndex === activeIndex}
                                        className={`tms-global-search__option ${flatIndex === activeIndex ? "active" : ""}`}
                                        onMouseEnter={() => setActiveIndex(flatIndex)}
                                        onClick={() => selectItem(item)}
                                    >
                                        <i className={`${item.icon} tms-global-search__option-icon`} aria-hidden="true" />
                                        <span className="tms-global-search__option-body">
                                            <span className="tms-global-search__option-label">{item.label}</span>
                                            <span className="tms-global-search__option-path">{item.subtitle}</span>
                                        </span>
                                    </button>
                                );
                            })}
                            {enquiryResults.length > 0 && (
                                <div className="tms-global-search__section">Enquiries</div>
                            )}
                            {enquiryResults.map((item, index) => {
                                const flatIndex = enquiryOffset + index;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        role="option"
                                        aria-selected={flatIndex === activeIndex}
                                        className={`tms-global-search__option ${flatIndex === activeIndex ? "active" : ""}`}
                                        onMouseEnter={() => setActiveIndex(flatIndex)}
                                        onClick={() => selectItem(item)}
                                    >
                                        <i className={`${item.icon} tms-global-search__option-icon`} aria-hidden="true" />
                                        <span className="tms-global-search__option-body">
                                            <span className="tms-global-search__option-label">{item.label}</span>
                                            <span className="tms-global-search__option-path">{item.subtitle}</span>
                                        </span>
                                    </button>
                                );
                            })}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
